/**
 * The support chat window. It loads only on the production host, so local
 * development, previews and the e2e stack never call the provider and never
 * leave the machine.
 */
export const SUPPORT_WIDGET_HOST = "app.sportsmet.com";
export const SUPPORT_WIDGET_SRC = "https://widget.hrc.lt/v1/loader.js";
export const SUPPORT_WIDGET_KEY = "wk_hysi62nrgiy3dzw7kb4j";

/**
 * Set on the root element while the support window is open, from the
 * widget's own open and close events. No stylesheet hides or moves the
 * widget any more: at phone width the button is hidden through the widget's
 * own `hideLauncher` (see `syncSupportLauncherToPhoneNav`), and the open
 * window is placed by the provider alone at every width.
 */
export const SUPPORT_WIDGET_OPEN_ATTRIBUTE = "data-support-widget-open";

/**
 * The provider's command function. Before its loader runs, a page may put a
 * stand-in on `window.HrcWidget` that only queues calls on `q`. The loader
 * replays that queue when it starts, so an `on` call made first is not lost.
 */
type typeSupportWidgetCommand = ((...args: unknown[]) => void) & {
  q?: unknown[][];
};

export type typeSupportWidgetWindow = {
  HrcWidget?: typeSupportWidgetCommand | undefined;
};

// The page's `window` is passed as a plain object: `HrcWidget` is the
// provider's global, not part of the DOM's own `Window` type.
function supportWidgetCommand(page: object): typeSupportWidgetCommand {
  const win = page as typeSupportWidgetWindow;
  const existing = win.HrcWidget;
  if (typeof existing === "function") return existing;
  const queue: typeSupportWidgetCommand = (...args: unknown[]) => {
    (queue.q ??= []).push(args);
  };
  win.HrcWidget = queue;
  return queue;
}

/** Mirrors the support window's open state onto the root element. */
export function trackSupportWidgetOpenState(
  root: Pick<Element, "setAttribute" | "removeAttribute">,
  win: object,
): void {
  const command = supportWidgetCommand(win);
  command("on", "open", () => {
    root.setAttribute(SUPPORT_WIDGET_OPEN_ATTRIBUTE, "");
  });
  command("on", "close", () => {
    root.removeAttribute(SUPPORT_WIDGET_OPEN_ATTRIBUTE);
  });
}

/** Whether this page is one the support chat loads on. */
export function isSupportWidgetHost(hostname: string): boolean {
  return hostname === SUPPORT_WIDGET_HOST;
}

/**
 * The signed-in fan as auth-api signed it (POST /support/identity-get). Sent
 * to the provider exactly as the server returned it: the hash covers this
 * externalId and this email, so any change on the way breaks it.
 */
export type typeSupportIdentity = {
  externalId: string;
  email: string;
  userHash: string;
};

/**
 * Tells the support chat who is writing, so it stops asking for an email.
 * Queued on the stand-in when the loader has not run yet; the loader replays
 * it after it starts itself from `data-widget-key`.
 */
export function identifySupportUser(
  win: object,
  identity: typeSupportIdentity,
): void {
  supportWidgetCommand(win)("identify", {
    externalId: identity.externalId,
    email: identity.email,
    userHash: identity.userHash,
  });
}

/**
 * A profile value the provider accepts. A date travels as an ISO string under
 * a key ending in `_at` or `_date`; `null` removes the key from the profile.
 */
export type typeSupportAttributeValue = string | number | boolean | null;

/**
 * The unsigned extras on top of the signed identity (`update`). The hash does
 * not cover them, so the provider labels them "from your site" and they must
 * never carry anything a decision depends on. `avatarUrl` is https only.
 */
export type typeSupportProfile = {
  name?: string;
  avatarUrl?: string | null;
  attributes?: Record<string, typeSupportAttributeValue>;
};

/** Tags to add to and take off the fan's contact (`setTags`). */
export type typeSupportTags = { add: string[]; remove: string[] };

/** Sent with every following message, not stored on the profile. */
export type typeSupportContext = {
  appVersion?: string;
  custom?: Record<string, string | number | boolean>;
};

/** Merges the extras into the identified fan's profile, field by field. */
export function updateSupportProfile(
  win: object,
  profile: typeSupportProfile,
): void {
  supportWidgetCommand(win)("update", profile);
}

export function setSupportTags(win: object, tags: typeSupportTags): void {
  supportWidgetCommand(win)("setTags", tags);
}

export function setSupportContext(
  win: object,
  context: typeSupportContext,
): void {
  supportWidgetCommand(win)("setContext", context);
}

/**
 * The provider caps a context value at 200 characters. A route pattern never
 * comes near it; the cut is for the one that someday does.
 */
const SUPPORT_CONTEXT_VALUE_MAX = 200;

/**
 * Tells the chat which screen the fan is on, with every following message.
 * The route PATTERN (`/pools/$poolId`), never the path: a path can name
 * another fan (`/fan/$username`), and nothing about other fans goes to the
 * provider. Sent once now and again on every resolved navigation to a
 * different pattern.
 */
export function trackSupportWidgetRoute({
  win,
  appVersion,
  currentRoute,
  subscribe,
}: {
  win: object;
  appVersion: string;
  currentRoute: () => string;
  subscribe: (listener: () => void) => unknown;
}): void {
  let lastRoute: string | null = null;
  const send = (): void => {
    const route = currentRoute().slice(0, SUPPORT_CONTEXT_VALUE_MAX);
    if (route === lastRoute) return;
    lastRoute = route;
    setSupportContext(win, { appVersion, custom: { surface: "app", route } });
  };
  send();
  subscribe(send);
}

/**
 * The language the chat speaks, taken from the page's own `lang` (`en-US` is
 * `en`). The product is English only, so without this a visitor whose browser
 * prefers another language would get a chat in a language the rest of the
 * page does not speak. Nothing is sent for a page with no `lang`.
 */
export function supportWidgetLocale(lang: string): string | null {
  const primary = lang.trim().split("-")[0]?.toLowerCase() ?? "";
  return /^[a-z]{2,3}$/.test(primary) ? primary : null;
}

/** Queues `setLocale` with the page's language, when it has one. */
export function setSupportWidgetLocale(win: object, lang: string): void {
  const locale = supportWidgetLocale(lang);
  if (locale !== null) supportWidgetCommand(win)("setLocale", locale);
}

/**
 * Forgets the identified fan and their conversations on this device. Called
 * on sign-out so the next person on the browser never sees them.
 */
export function resetSupportUser(win: object): void {
  supportWidgetCommand(win)("reset");
}

/**
 * Reports a refused identify (IDENTITY_INVALID and friends). Only the code is
 * reported, never the identity: the hash is a credential toward the provider.
 * The chat keeps working anonymously, so this is a warning and nothing more.
 */
export function reportSupportIdentifyFailure(code: string): void {
  console.warn(`#20261104-app01 support chat identify refused: ${code}`);
}

/** Listens for a refused identify and hands its code to `report`. */
export function trackSupportIdentifyFailures(
  win: object,
  report: (code: string) => void = reportSupportIdentifyFailure,
): void {
  supportWidgetCommand(win)("on", "identify-failed", (event: unknown) => {
    const code =
      typeof event === "object" &&
      event !== null &&
      typeof (event as { code?: unknown }).code === "string"
        ? (event as { code: string }).code
        : "UNKNOWN";
    report(code);
  });
}

export function loadSupportWidget(
  hostname: string,
  doc: Pick<Document, "createElement" | "body" | "documentElement">,
  win: object,
): boolean {
  if (!isSupportWidgetHost(hostname)) {
    return false;
  }
  // Registered before the loader is added, so the queue holds the listeners
  // when the loader replays it. No `init` is queued, so the loader still
  // starts itself from the script's `data-widget-key`.
  trackSupportWidgetOpenState(doc.documentElement, win);
  trackSupportIdentifyFailures(win);
  const script = doc.createElement("script");
  script.src = SUPPORT_WIDGET_SRC;
  script.async = true;
  script.dataset.widgetKey = SUPPORT_WIDGET_KEY;
  doc.body.appendChild(script);
  return true;
}

/**
 * The phone width, the same `(width < 40rem)` the one media query in
 * `index.css` uses to fix `.sm-section-nav` to the bottom of the viewport.
 * At this width the bottom bar's Help item opens the chat, so the provider's
 * floating button is hidden and never covers a tab. From 40rem up there is
 * no bar and no Help item, so the button is shown.
 */
export const SUPPORT_LAUNCHER_HIDDEN_QUERY = "(width < 40rem)";

/**
 * Opens the support chat window, from the phone bar's Help item. Off the
 * production host nothing loads the widget, so this only lands in the
 * stand-in's queue and nothing happens. It never throws.
 */
export function openSupportChat(win: object): void {
  supportWidgetCommand(win)("open");
}

type typeLauncherMediaQuery = Pick<
  MediaQueryList,
  "matches" | "addEventListener" | "removeEventListener"
>;

/**
 * Whether a Help item that opens the chat is on screen, per page window. Only
 * the signed-in shell's bottom bar has one. A signed-out phone screen (sign
 * in, the welcome tour) has none, so it keeps the provider's button: it is
 * the only way to reach support there.
 */
const helpNavState = new WeakMap<
  object,
  { present: boolean; apply: (() => void) | null }
>();

function helpNavFor(win: object): {
  present: boolean;
  apply: (() => void) | null;
} {
  let state = helpNavState.get(win);
  if (!state) {
    state = { present: false, apply: null };
    helpNavState.set(win, state);
  }
  return state;
}

/**
 * Called by the bottom bar when its Help item mounts (true) and unmounts
 * (false). Re-applies the button state right away when the sync is running.
 */
export function setSupportHelpNavPresent(win: object, present: boolean): void {
  const state = helpNavFor(win);
  state.present = present;
  state.apply?.();
}

/**
 * Hides the floating support button only while the page is at phone width
 * AND the bottom bar's Help item is on screen, and shows it otherwise,
 * following every resize, rotation and mount or unmount of the bar. Only the
 * button is hidden: `open` still opens the window at any width. The state is
 * sent once now (queued ahead of the loader on a fresh page) and again on the
 * widget's `ready`, so a provider that draws its default button when it
 * starts cannot undo it. Returns a function that stops following and shows
 * the button again.
 */
export function syncSupportLauncherToPhoneNav(
  win: object,
  matchMedia: (query: string) => typeLauncherMediaQuery,
): () => void {
  const command = supportWidgetCommand(win);
  const query = matchMedia(SUPPORT_LAUNCHER_HIDDEN_QUERY);
  const helpNav = helpNavFor(win);
  const apply = (): void => {
    supportWidgetCommand(win)(
      query.matches && helpNav.present ? "hideLauncher" : "showLauncher",
    );
  };
  helpNav.apply = apply;
  apply();
  command("on", "ready", apply);
  query.addEventListener("change", apply);
  return () => {
    if (helpNav.apply === apply) helpNav.apply = null;
    query.removeEventListener("change", apply);
    supportWidgetCommand(win)("off", "ready", apply);
    supportWidgetCommand(win)("showLauncher");
  };
}

/**
 * The page's one support chat start, called from `main.tsx` before the first
 * render. On the production host it loads the widget and then starts the
 * phone-width button sync, so the hide is queued behind the loader's
 * listeners and replayed as soon as the loader starts. Anywhere else it does
 * nothing at all: no script, no stand-in, no media query. Returns whether the
 * widget was loaded.
 */
export function startSupportWidget(
  hostname: string,
  doc: Pick<Document, "createElement" | "body" | "documentElement">,
  win: object,
  matchMedia: (query: string) => typeLauncherMediaQuery,
): boolean {
  if (!loadSupportWidget(hostname, doc, win)) return false;
  // At phone width the bottom bar's Help item opens the chat, so the
  // floating button is hidden there for the life of the page, and shown
  // from 40rem up.
  syncSupportLauncherToPhoneNav(win, matchMedia);
  return true;
}
